import { useEffect, useState } from "react";
import { Loader2, Save, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Setting, updateSetting } from "@/Api/settings";
import {
  VALUE_TYPE_LABELS,
  DURATION_VALUE_TYPES,
  DATE_RANGE_VALUE_TYPE,
} from "@/constants/settings";
import { refreshSettings, useSettings } from "@/hooks/useSettings";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

const isDuration = (valueType: string) =>
  (DURATION_VALUE_TYPES as readonly string[]).includes(valueType);

/** Lê o JSON do tipo `date_range` sem quebrar com valor vazio ou inválido. */
const parseDateRange = (value: string) => {
  if (!value) return { start_date: "", end_date: "" };

  try {
    const parsed = JSON.parse(value);

    return {
      start_date: parsed?.start_date ?? "",
      end_date: parsed?.end_date ?? "",
    };
  } catch {
    return { start_date: "", end_date: "" };
  }
};

/** Serializa o período no formato canônico gravado pela API. */
const stringifyDateRange = (startDate: string, endDate: string) => {
  if (!startDate && !endDate) return "";

  return JSON.stringify({
    start_date: startDate || null,
    end_date: endDate || null,
  });
};

/**
 * Período: duas datas, gravadas juntas em uma única configuração.
 */
const DateRangeField = ({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) => {
  const range = parseDateRange(value);

  return (
    <div className="flex flex-wrap gap-4 flex-1">
      <div className="space-y-1">
        <Label className="text-sm font-medium">Data Inicial</Label>
        <Input
          type="date"
          value={range.start_date}
          max={range.end_date || undefined}
          onChange={(e) =>
            onChange(stringifyDateRange(e.target.value, range.end_date))
          }
          disabled={disabled}
          className="w-[190px]"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-sm font-medium">Data Final</Label>
        <Input
          type="date"
          value={range.end_date}
          min={range.start_date || undefined}
          onChange={(e) =>
            onChange(stringifyDateRange(range.start_date, e.target.value))
          }
          disabled={disabled}
          className="w-[190px]"
        />
      </div>
    </div>
  );
};

/**
 * Campo de edição adequado ao value_type da configuração.
 */
const SettingField = ({
  setting,
  value,
  onChange,
  disabled,
}: {
  setting: Setting;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) => {
  if (setting.value_type === "boolean") {
    return (
      <Switch
        checked={value === "true"}
        onCheckedChange={(checked) => onChange(checked ? "true" : "false")}
        disabled={disabled}
      />
    );
  }

  if (setting.value_type === "text" || setting.value_type === "json") {
    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={setting.value_type === "json" ? 6 : 4}
        className="flex-1 font-mono text-sm"
        placeholder={setting.value_type === "json" ? "{ }" : "Digite o valor"}
      />
    );
  }

  if (setting.value_type === DATE_RANGE_VALUE_TYPE) {
    return (
      <DateRangeField value={value} onChange={onChange} disabled={disabled} />
    );
  }

  if (setting.value_type === "date" || setting.value_type === "datetime") {
    return (
      <Input
        type={setting.value_type === "date" ? "date" : "datetime-local"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="flex-1"
      />
    );
  }

  if (
    isDuration(setting.value_type) ||
    setting.value_type === "integer" ||
    setting.value_type === "decimal"
  ) {
    return (
      <div className="flex items-center gap-2 flex-1">
        <Input
          type="number"
          min={isDuration(setting.value_type) ? 1 : undefined}
          step={setting.value_type === "decimal" ? "0.01" : "1"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="flex-1"
          placeholder="Digite o valor"
        />
        {isDuration(setting.value_type) && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {VALUE_TYPE_LABELS[setting.value_type]?.toLowerCase()}
          </span>
        )}
      </div>
    );
  }

  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="flex-1"
      placeholder="Digite o valor"
    />
  );
};

/**
 * Painel genérico das configurações do sistema.
 *
 * Cada configuração é salva individualmente, e o formato do campo de edição
 * vem do value_type definido no cadastro da chave.
 */
export const SystemSettings = () => {
  const { settings, loading } = useSettings();
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    setEditingValues(
      settings.reduce<Record<string, string>>((acc, setting) => {
        acc[setting.uuid] = setting.value ?? "";
        return acc;
      }, {})
    );
  }, [settings]);

  const persist = async (
    setting: Setting,
    payload: { value?: string | null; is_active?: boolean }
  ) => {
    setUpdating(setting.uuid);

    try {
      const { data, ok } = await updateSetting(setting.uuid, payload);

      if (!ok) {
        toast.error(data?.message ?? "Erro ao atualizar configuração");
        return;
      }

      toast.success("Configuração atualizada com sucesso!");
      await refreshSettings();
    } catch (error) {
      toast.error("Erro ao atualizar configuração");
    } finally {
      setUpdating(null);
    }
  };

  const handleSave = (setting: Setting) => {
    const newValue = editingValues[setting.uuid] ?? "";

    if (newValue === (setting.value ?? "")) {
      toast.info("Nenhuma alteração detectada");
      return;
    }

    persist(setting, { value: newValue });
  };

  const handleToggleActive = (setting: Setting) =>
    persist(setting, { is_active: !setting.is_active });

  if (loading && settings.length === 0) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (settings.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5" />
          Configurações Gerais
        </CardTitle>
        <CardDescription>
          Parâmetros que alteram o comportamento do sistema para esta conta.
          Cada configuração é salva individualmente.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {settings.map((setting) => {
            const isUpdating = updating === setting.uuid;
            const currentValue = editingValues[setting.uuid] ?? "";
            const hasChanges = currentValue !== (setting.value ?? "");

            return (
              <div
                key={setting.uuid}
                className="space-y-3 pb-6 border-b last:border-b-0 last:pb-0"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-base font-semibold">
                        {setting.name}
                      </Label>
                      <Badge variant="outline" className="text-xs">
                        {VALUE_TYPE_LABELS[setting.value_type] ??
                          setting.value_type}
                      </Badge>
                      {!setting.is_active && (
                        <Badge className="bg-gray-500 text-white">Inativa</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 font-mono">
                      {setting.key}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Ativa</span>
                    <Switch
                      checked={setting.is_active}
                      onCheckedChange={() => handleToggleActive(setting)}
                      disabled={isUpdating}
                    />
                  </div>
                </div>

                {setting.value_type === "boolean" ? (
                  <div className="flex items-center gap-3">
                    <SettingField
                      setting={setting}
                      value={currentValue}
                      onChange={(value) =>
                        setEditingValues((prev) => ({
                          ...prev,
                          [setting.uuid]: value,
                        }))
                      }
                      disabled={isUpdating || !setting.is_active}
                    />
                    <Button
                      onClick={() => handleSave(setting)}
                      disabled={isUpdating || !hasChanges || !setting.is_active}
                      className={cn(colors.bg_primary)}
                    >
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salvar
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2 items-start">
                    <SettingField
                      setting={setting}
                      value={currentValue}
                      onChange={(value) =>
                        setEditingValues((prev) => ({
                          ...prev,
                          [setting.uuid]: value,
                        }))
                      }
                      disabled={isUpdating || !setting.is_active}
                    />
                    <Button
                      onClick={() => handleSave(setting)}
                      disabled={isUpdating || !hasChanges || !setting.is_active}
                      className={cn(colors.bg_primary)}
                    >
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salvar
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemSettings;
